import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteOrganisationById, getOrganisationById, getUsersEmails, updateOrganisation } from '../../firebase';
import { toast } from 'react-toastify';
import { Organisation, csvContent } from '../../interfaces';
import { useTranslation } from 'react-i18next';

import {
  CommandBarButton,
  DetailsList,
  DetailsListLayoutMode,
  FontIcon,
  IColumn,
  IContextualMenuItem,
  MarqueeSelection,
  Selection,
  SelectionMode,
  TextField
} from '@fluentui/react';
import {
  Button,
  CloseButton,
  Container,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import { FirebaseError } from '@firebase/util';
import { useAuthState } from '../../hooks/useAuthState';
import FABBackButton from '../../components/FABBackButton';
import { splitPromoPrefix } from '../../utils/helperFunctions';

type customUsersTableType = { email: string; isAdmin: boolean; isMember: boolean };
type formErrorType = { name: boolean; seats: boolean; duplicates: string[] };
const EditOrganisation = () => {
  const params = useParams<{ orgId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userRoles } = useAuthState();
  const [error, setError] = useState<formErrorType>({ name: false, seats: false, duplicates: [] });
  const [organisation, setOrganisation] = useState<Organisation>();
  const [seats, setSeats] = useState<string>('');
  const [name, setName] = useState<string>('');

  const [newAdmins, setNewAdmins] = useState<string>('');
  const [newMembers, setNewMembers] = useState<string>('');
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoCodePrefix, setPromoCodePrefix] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const adminPickerRef = useRef<HTMLInputElement>(null);
  const memberPickerRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState<string>('');
  const [columns, setColumns] = useState<IColumn[]>([]);

  const [totalCount, setTotalCount] = useState<number>(0);
  const [allUsers, setAllUSers] = useState<customUsersTableType[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<customUsersTableType[]>([]);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<string>('');

  /** Fetch the organisation by the organisation ID from URL params */
  useEffect(() => {
    if (!params.orgId) {
      toast.warn('something went wrong!');
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const _org = await getOrganisationById(params.orgId!);
        setOrganisation(_org);
      } catch (e) {
        console.log('fetch org with ID,', params.orgId, ' failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [params.orgId, userRoles]);

  useEffect(() => {
    if (!organisation) return;
    (async () => {
      let _users: string[] = [];
      if (userRoles.isSuperAdmin) _users = await getUsersEmails();
      const nonDuplicated = [...new Set([...organisation.admins, ...organisation.members, ..._users])];
      const _allUsers: customUsersTableType[] = [];
      nonDuplicated.forEach((user) => {
        let temp: customUsersTableType = { email: '', isAdmin: false, isMember: false };
        temp.email = user;
        if (organisation.admins.includes(user)) temp.isAdmin = true;
        if (organisation.members.includes(user)) temp.isMember = true;
        _allUsers.push(temp);
      });
      setAllUSers(_allUsers);
    })();
    setSeats(`${organisation.seats}`);
    const promoCodeSplit = splitPromoPrefix(organisation.promoCode);
    setPromoCodePrefix(promoCodeSplit[0]);
    setPromoCode(promoCodeSplit[1]);
    setName(organisation.name);
  }, [organisation, userRoles.isSuperAdmin]);

  /** Total users count for comparing with available seats */
  const allAdminsRef = useRef<string[]>();
  const allMembersRef = useRef<string[]>();
  useEffect(() => {
    if (!organisation) return;
    const _newAdmins = newAdmins.trim() === '' ? [] : newAdmins.trim().split(',');
    const _newMembers = newMembers.trim() === '' ? [] : newMembers.trim().split(',');
    const _admins = allUsers.filter((u) => u.isAdmin).map((u) => u.email);
    const _members = allUsers.filter((u) => u.isMember).map((u) => u.email);
    allAdminsRef.current = [..._newAdmins, ..._admins];
    allMembersRef.current = [..._newMembers, ..._members];
    setTotalCount(_admins.length + _members.length + _newAdmins.length + _newMembers.length);
  }, [allUsers, newAdmins, newMembers, organisation]);

  const findDuplicateValues = (arrays: string[][]) => {
    const duplicates = new Set<string>();
    const seenValues = new Set<string>();

    for (const array of arrays) {
      for (const value of array) {
        if (seenValues.has(value)) {
          duplicates.add(value);
        } else {
          seenValues.add(value);
        }
      }
    }

    return Array.from(duplicates);
  };
  const isValid = () => {
    const temp: formErrorType = { name: false, seats: false, duplicates: [] };
    if (name.trim() === '') temp.name = true;
    if (totalCount > (!isNaN(parseInt(seats, 10)) ? parseInt(seats, 10) : 0)) temp.seats = true;
    const _newAdmins = newAdmins.trim() === '' ? [] : newAdmins.trim().split(',');
    const _newMembers = newMembers.trim() === '' ? [] : newMembers.trim().split(',');
    const _admins = allUsers.filter((u) => u.isAdmin).map((u) => u.email);
    const _members = allUsers.filter((u) => u.isMember).map((u) => u.email);
    const duplicatesArray = findDuplicateValues([_newAdmins, _newMembers, _admins, _members]);
    temp.duplicates = duplicatesArray;
    setError(temp);
    return temp;
  };

  const onFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const validation = isValid();
    if (validation.name || validation.seats || validation.duplicates.length > 0) {
      validation.name && toast.error(t('organisations.name-error'));
      validation.seats && toast.error(t('organisations.not-enough-seats'));
      validation.duplicates.length > 0 &&
        toast.error(
          <div>
            <p>{t('organisations.duplicate-found')}</p>
            <ul>
              {validation.duplicates.map((i) => (
                <li>{i}</li>
              ))}
            </ul>
          </div>
        );
      return;
    }
    setLoading(true);
    const _admins = allUsers.filter((u) => u.isAdmin).map((u) => u.email);
    const _members = allUsers.filter((u) => u.isMember).map((u) => u.email);
    const _newAdmins =
      newAdmins.trim() === ''
        ? []
        : newAdmins
            .trim()
            .split(',')
            .filter((a) => !_admins.includes(a));
    const _newMembers =
      newMembers.trim() === ''
        ? []
        : newMembers
            .trim()
            .split(',')
            .filter((m) => !_members.includes(m));
    try {
      const _updatedOrg = await updateOrganisation(
        {
          name,
          seats: !isNaN(parseInt(seats, 10)) ? parseInt(seats, 10) : 0,
          admins: _admins.concat(_newAdmins),
          members: _members.concat(_newMembers),
          promoCode: promoCodePrefix + promoCode
        },
        organisation!.id
      );

      if (_updatedOrg) {
        toast.success(t('organisations.org-updated'));
        setOrganisation(_updatedOrg);
        setNewAdmins('');
        setNewMembers('');
      }
    } catch (e) {
      toast.error((e as FirebaseError).message.split(':')[0]);
      console.log('update organisation failed:', e);
    } finally {
      setLoading(false);
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: csvContent) => {
    if (e.target.files) {
      try {
        const file = e.target.files[0];
        const fileUrl = URL.createObjectURL(file);
        const response = await fetch(fileUrl);
        const text = await response.text();
        const lines = text.split(/\r\n|\n|\r/);
        const _data = lines.map((line) => line.split(','));

        if (type === csvContent.admins) {
          setNewAdmins(_data.flat().toString());
        }
        if (type === csvContent.members) {
          setNewMembers(_data.flat().toString());
        }
        e.target.files = null;
        e.target.value = '';
      } catch (error) {
        console.error(error);
      }
    }
  };

  /** Assign state to its corresponding reference */
  const allUsersRef = useRef<customUsersTableType[]>([]);
  const filteredUsersRef = useRef<customUsersTableType[]>([]);
  useEffect(() => {
    allUsersRef.current = allUsers;
    filteredUsersRef.current = filteredUsers;
  }, [allUsers, filteredUsers]);

  /** Search query */
  useEffect(() => {
    if (query.trim() === '') {
      setFilteredUsers([]);
      return;
    }
    if (query.trim() !== '' && allUsers.length > 0) {
      const _filteredUsers = allUsers.filter((u) => u.email.toLowerCase().includes(query.toLowerCase()));
      setFilteredUsers(_filteredUsers);
    }
  }, [allUsers, query]);
  const _onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter((currCol) => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    const newItems = _copyAndSort(currColumn.fieldName!, currColumn.isSortedDescending);
    setColumns(newColumns);
    setAllUSers(newItems);
  };

  const _copyAndSort = (columnKey: string, isSortedDescending?: boolean): customUsersTableType[] => {
    const key = columnKey as keyof customUsersTableType;
    return (filteredUsersRef.current.length > 0 ? filteredUsersRef : allUsersRef).current
      .slice(0)
      .sort((a: customUsersTableType, b: customUsersTableType) =>
        (isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1
      );
  };

  useEffect(() => {
    setColumns([
      {
        key: 'email',
        name: 'Email',
        fieldName: 'email',
        data: 'string',
        minWidth: 210,
        maxWidth: 350,
        isRowHeader: true,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        sortAscendingAriaLabel: 'Sorted A to Z',
        sortDescendingAriaLabel: 'Sorted Z to A',
        isPadded: true,
        onColumnClick: _onColumnClick
      },
      {
        key: 'role',
        name: 'Roll',
        fieldName: '',
        data: 'boolean',
        minWidth: 30,
        maxWidth: 30,
        isResizable: true,
        onRender: (item: customUsersTableType) =>
          item.isAdmin ? (
            <FontIcon
              aria-label='AuthenticatorApp'
              iconName='AuthenticatorApp'
              style={{ fontSize: 27, color: 'skyblue' }}
            />
          ) : item.isMember ? (
            <FontIcon aria-label='Contact' iconName='Contact' style={{ fontSize: 25, color: 'green' }} />
          ) : null
      },
      {
        key: 'button',
        name: '',

        minWidth: 45,
        maxWidth: 55,
        isResizable: true,
        onRender: (item: customUsersTableType) => {
          const dropDownOptions: IContextualMenuItem[] = [];
          if (item.isAdmin)
            dropDownOptions.push({
              key: 'removeAdmin',
              text: 'Ta bort administratör',
              iconProps: { iconName: 'AuthenticatorApp', style: { color: 'red' } },
              onClick: () => {
                const _allUsers = allUsers.map((u) => (u.email === item.email ? { ...u, isAdmin: false } : u));
                setAllUSers([..._allUsers]);
              }
            });
          if (item.isMember)
            dropDownOptions.push({
              key: 'removeMember',
              text: 'Ta bort medlem',
              iconProps: { iconName: 'Contact', style: { color: 'red' } },
              onClick: () => {
                const _allUsers = allUsers.map((u) => (u.email === item.email ? { ...u, isMember: false } : u));
                setAllUSers([..._allUsers]);
              }
            });
          if (!item.isAdmin)
            dropDownOptions.push({
              key: 'addAsAdmin',
              text: 'Lägg till som administratör',
              iconProps: { iconName: 'AuthenticatorApp' },
              onClick: () => {
                if (totalCount >= (!isNaN(parseInt(seats, 10)) ? parseInt(seats, 10) : 0)) {
                  toast.error(t('organisations.not-enough-seats'));
                  return;
                }
                const _allUsers = allUsers.map((u) =>
                  u.email === item.email ? { ...u, isAdmin: true, isMember: false } : u
                );
                setAllUSers([..._allUsers]);
              }
            });
          if (!item.isMember)
            dropDownOptions.push({
              key: 'addAsMember',
              text: 'Lägg till som medlem',
              iconProps: { iconName: 'Contact' },
              onClick: () => {
                if (totalCount >= (!isNaN(parseInt(seats, 10)) ? parseInt(seats, 10) : 0)) {
                  toast.error(t('organisations.not-enough-seats'));
                  return;
                }
                const _allUsers = allUsers.map((u) =>
                  u.email === item.email ? { ...u, isMember: true, isAdmin: false } : u
                );
                setAllUSers([..._allUsers]);
              }
            });
          return (
            <div className='d-flex flex-row justify-content-end'>
              <CommandBarButton
                iconProps={{ iconName: 'EditContact', style: { fontSize: 25, color: 'black', fontWeight: 'bold' } }}
                styles={{ root: { height: '2rem', width: '4rem' } }}
                menuProps={{
                  items: dropDownOptions
                }}
              />
            </div>
          );
        }
      }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, seats, totalCount]);

  const deleteOrganisation = async () => {
    if (!organisation) return;
    setLoading(true);
    try {
      await deleteOrganisationById(organisation.id);
      setLoading(false);
      toast.success(t('organisations.delete-org-success'));
      navigate('/orgs');
    } catch (e) {
      console.log('deleteOrganisation failed:', e);
      toast.error(t('organisation.delete-org-failed'));
    } finally {
    }
  };

  const tableSelection = new Selection({
    onSelectionChanged: () => {
      prepareEmailList();
    },
    getKey: (item: any) => `${item.key}`
  });

  const prepareEmailList = () => {
    const items = tableSelection.getSelection();
    const _recipients = items.map((i) => i.email);
    setRecipients(_recipients.toString());
  };
  const sendEmail = () => {
    if (recipients.trim() === '') {
      toast.error('Select users to send email to');
      return;
    }
    const mailUrl = `mailto:${recipients}?subject=Inbjudan till The Enzo Way&body=Du har blivit inbjuden till The Enzo Way.%0D%0ALadda ner appen här https://enzogroup.page.link/invite`;
    window.open(mailUrl);
  };

  const copyToClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(promoCodePrefix + promoCode);
      toast.info(t('organisations.copy-success'));
    } catch (e) {
      toast.error('');
    }
  };
  return organisation ? (
    <Container>
      <div>
        <FABBackButton to='/orgs' />
        {userRoles.isSuperAdmin && (
          <Button
            style={{ position: 'absolute', top: '5rem', right: '2rem', fontSize: '12px' }}
            variant='danger'
            onClick={() => setShowDeleteModal(true)}
            disabled={loading}
          >
            <div className='d-flex flex-row justify-content-center align-items-center'>
              <i className='fa-solid fa-trash-can' style={{ fontSize: '25px', marginRight: '1rem' }}></i>
              {t('organisations.delete-org-modal-title')}
            </div>
          </Button>
        )}
        <h1 className='h1 mb-3'>{t('organisations.editOrganisation')}</h1>
        <div className='d-flex flex-row justify-content-between align-items-center m-4'>
          <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.seats-number')}</Tooltip>}>
            <div className='d-flex flex-row justify-content-center align-items-center'>
              <i className='fa-solid fa-chair fas me-2' style={{ fontSize: 25 }}></i>
              <p className='m-0'>{seats}</p>
            </div>
          </OverlayTrigger>
          <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.admins-number')}</Tooltip>}>
            <div className='d-flex flex-row justify-content-center align-items-center'>
              <i className='fa-solid fa-user-shield fas me-2' style={{ fontSize: 25 }}></i>
              <p className='m-0'>{allAdminsRef.current?.length}</p>
            </div>
          </OverlayTrigger>
          <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.members-number')}</Tooltip>}>
            <div className='d-flex flex-row justify-content-center align-items-center'>
              <i className='fa-solid fa-user fas me-2' style={{ fontSize: 25 }}></i>
              <p className='m-0'>{allMembersRef.current?.length}</p>
            </div>
          </OverlayTrigger>
        </div>

        <div className='text-start'>
          <form onSubmit={onFormSubmit} noValidate>
            <button type='submit' className='btn btn-primary w-100 my-4' disabled={loading}>
              {!loading ? (
                t('organisations.savechanges')
              ) : (
                <Spinner animation='border' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </Spinner>
              )}
            </button>
            <div className='form-group'>
              <label className='d-flex mt-3' htmlFor='organisationName'>
                {t('organisations.organisationName')}
              </label>
              <input
                type='text'
                className={`form-control my-2 ${error.name ? 'is-invalid' : ''}`}
                id='organisationName'
                aria-describedby='emailHelp'
                placeholder={t('organisations.organisationName')}
                required
                value={name}
                onChange={(e) => {
                  if (error.name) setError({ ...error, name: false });
                  setName(e.target.value);
                }}
              />
              <div className='invalid-feedback'>{t('organisations.name-error')}</div>
            </div>
            {userRoles.isSuperAdmin && (
              <div className='form-group'>
                <label htmlFor='organisationSeats' className='d-flex mt-3'>
                  {t('organisations.seats')}
                </label>
                <input
                  type='number'
                  className={`form-control my-2 ${error.seats ? 'is-invalid' : ''}`}
                  id='organisationSeats'
                  placeholder={t('organisations.seats')}
                  value={seats}
                  min={0}
                  onChange={(e) => {
                    if (error.seats) setError({ ...error, seats: false });
                    setSeats(e.target.value);
                  }}
                />
                <div className='invalid-feedback'>{t('organisations.not-enough-seats')}</div>
              </div>
            )}
            <Form.Label htmlFor='promo-code' className='d-flex mt-3'>
              {t('organisations.promoCode')}
            </Form.Label>
            <InputGroup className='mb-3 my-2'>
              <InputGroup.Text
                id='basic-addon3'
                style={{
                  backgroundColor: '#0e1422',
                  border: 'none',
                  borderTopLeftRadius: '40px',
                  borderBottomLeftRadius: '40px'
                }}
              >
                {promoCodePrefix}
              </InputGroup.Text>
              <Form.Control
                id='promo-code'
                aria-describedby='basic-addon3'
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
              <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.copy-promo')}</Tooltip>}>
                <InputGroup.Text
                  id='basic-addon3'
                  style={{
                    backgroundColor: 'lightgrey',
                    border: 'none',
                    borderTopRightRadius: '40px',
                    borderBottomRightRadius: '40px',
                    cursor: 'pointer'
                  }}
                  onClick={copyToClipBoard}
                >
                  <i className='fa-solid fa-copy' style={{ fontSize: 25, color: 'black' }}></i>
                </InputGroup.Text>
              </OverlayTrigger>
            </InputGroup>
            <div className='form-group inli'>
              <label htmlFor='organisationAdmins' className='d-flex mt-3'>
                {t('organisations.admins')}
                <span style={{ fontStyle: 'italic' }}>&nbsp;{t('organisations.emailsListInfo')}</span>
              </label>
              <div className='input-group flex-nowrap'>
                <button
                  type='button'
                  className='input-group-text input-span-left'
                  onClick={() => setShowInfoModal(true)}
                >
                  <i className='fa-solid fa-circle-question' style={{ fontSize: 25, color: 'black' }} />
                </button>

                <input
                  type='text'
                  className={`form-control my-2`}
                  id='organisationAdmins'
                  placeholder={t('organisations.admins')}
                  value={newAdmins}
                  min={0}
                  onChange={(e) => setNewAdmins(e.target.value)}
                />
                <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.import-csv')}</Tooltip>}>
                  <span
                    style={{ cursor: 'pointer' }}
                    className='input-group-text input-span-right'
                    id='addon-wrapping'
                    onClick={() => {
                      adminPickerRef.current?.click();
                    }}
                  >
                    <i className='fa-solid fa-file-csv' style={{ fontSize: 25, color: 'black' }} />
                    <input
                      ref={adminPickerRef}
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(e, csvContent.admins)}
                      accept='.csv'
                    ></input>
                  </span>
                </OverlayTrigger>
              </div>
            </div>
            <div className='form-group'>
              <label htmlFor='organisationAdmins' className='d-flex mt-3'>
                {t('organisations.members')}
                <span style={{ fontStyle: 'italic' }}>&nbsp;{t('organisations.emailsListInfo')}</span>
              </label>
              <div className='input-group flex-nowrap'>
                <button
                  type='button'
                  className='input-group-text input-span-left'
                  onClick={() => setShowInfoModal(true)}
                >
                  <i className='fa-solid fa-circle-question' style={{ fontSize: 25, color: 'black' }} />
                </button>
                <input
                  type='text'
                  className={`form-control my-2`}
                  id='organisationAdmins'
                  placeholder={t('organisations.members')}
                  value={newMembers}
                  min={0}
                  onChange={(e) => setNewMembers(e.target.value)}
                />
                <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.import-csv')}</Tooltip>}>
                  <span
                    style={{ cursor: 'pointer' }}
                    className='input-group-text input-span-right'
                    id='addon-wrapping'
                    onClick={() => {
                      memberPickerRef.current?.click();
                    }}
                  >
                    <i className='fa-solid fa-file-csv' style={{ fontSize: 25, color: 'black' }} />
                    <input
                      ref={memberPickerRef}
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(e, csvContent.members)}
                      accept='.csv'
                    ></input>
                  </span>
                </OverlayTrigger>
              </div>
            </div>

            <div className='d-flex flex-row justify-content-between align-items-center mt-2'>
              <div style={{ width: '15rem', marginTop: '1.5rem' }}>
                <TextField
                  iconProps={{ iconName: 'Search' }}
                  styles={{ fieldGroup: { height: '2.5rem' }, icon: { fontSize: 25, fontWeight: 'bold' } }}
                  underlined
                  onChange={(e, str) => setQuery(str ?? '')}
                />
              </div>
              <Button
                style={{ fontSize: 12, padding: '12px' }}
                variant='info'
                disabled={recipients.trim() === ''}
                onClick={() => sendEmail()}
              >
                <i className='fa-solid fa-envelope' style={{ fontSize: 16, marginRight: '5px' }}></i>Skicka inbjudningar
              </Button>
            </div>
            <div style={{ width: '100%', height: '70vh', overflow: 'scroll', marginTop: '.5rem' }}>
              <MarqueeSelection selection={tableSelection}>
                <DetailsList
                  items={query.trim() !== '' ? filteredUsers : allUsers}
                  compact={false}
                  columns={columns}
                  selectionMode={SelectionMode.multiple}
                  getKey={(item) => item.id}
                  setKey='none'
                  layoutMode={DetailsListLayoutMode.justified}
                  isHeaderVisible={true}
                  selection={tableSelection}
                  selectionPreservedOnEmptyClick={true}
                  styles={{ root: { border: '2px solid #a701fc', borderRadius: '5px' } }}
                />
              </MarqueeSelection>
            </div>
          </form>
        </div>
        <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)}>
          <Modal.Header>
            <CloseButton style={{ backgroundColor: 'lightgrey' }} onClick={() => setShowInfoModal(false)} />
          </Modal.Header>
          <Modal.Body>
            <ul>
              <li>Tillagda e-postadresser kommer inte att ersätta befintliga användarna. </li>
              <li>Tillagda e-postadresser kommer endast att läggas till i den befintliga listan, se tabellen nedan.</li>
              <li>För att ta bort en medlem eller administratör, använd tabellen nedan.</li>
              <li>
                Du kan även ändra behörigheten medlem till administratör och tvärtom genom att använda tabellen nedan.
              </li>
            </ul>
          </Modal.Body>
        </Modal>
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header>
            <div className='d-flex flex-row justify-content-center align-items-center'>
              <i
                className='fa-solid fa-trash-can'
                style={{ fontSize: 30, fontWeight: 'bold', marginRight: '15px', color: 'white' }}
              ></i>
              <h4 style={{ margin: 0 }}>{t('organisations.delete-org-modal-title')}</h4>
            </div>
            <CloseButton style={{ backgroundColor: 'lightgrey' }} onClick={() => setShowDeleteModal(false)} />
          </Modal.Header>
          <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>
            <p style={{ fontWeight: 'bold' }}>Är du verkligen säker på att du vill radera organisationen?</p>
            <ul>
              <li>Alla organisationens medlemmer kommer att försvinna</li>
              <li>Alla organisationens administratörer kommer att försvinna</li>
              <li>Företagskoden kommer att inaktiveras</li>
            </ul>
          </Modal.Body>
          <Modal.Footer className='d-flex flex-row justify-content-between align-items-center'>
            <Button variant='success' onClick={() => setShowDeleteModal(false)} disabled={loading}>
              {loading ? <Spinner animation='border'></Spinner> : t('organisations.cancel')}
            </Button>
            <Button variant='danger' onClick={() => deleteOrganisation()} disabled={loading}>
              {loading ? <Spinner animation='border'></Spinner> : t('organisations.delete-org-force')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  ) : (
    <Container>
      <div>
        <div className='text-center'>
          <h4>{t('organisations.notfound')}</h4>
        </div>
      </div>
    </Container>
  );
};

export default EditOrganisation;
